import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ParsedResume } from '@/lib/resume-parser';

interface ResumeContextType {
  pendingFile: File | null;
  setPendingFile: (file: File | null) => void;
  parsedResume: ParsedResume | null;
  setParsedResume: (data: ParsedResume | null) => void;
  clearResumeData: () => void;
  hasPendingResume: boolean;
}

const ResumeContext = createContext<ResumeContextType>({
  pendingFile: null,
  setPendingFile: () => {},
  parsedResume: null,
  setParsedResume: () => {},
  clearResumeData: () => {},
  hasPendingResume: false,
});

interface ResumeProviderProps {
  children: ReactNode;
}

export function ResumeProvider({ children }: ResumeProviderProps) {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [parsedResume, setParsedResume] = useState<ParsedResume | null>(null);

  const hasPendingResume = !!(pendingFile || parsedResume);

  const clearResumeData = () => {
    setPendingFile(null);
    setParsedResume(null);
  };

  return (
    <ResumeContext.Provider
      value={{
        pendingFile,
        setPendingFile,
        parsedResume,
        setParsedResume,
        clearResumeData,
        hasPendingResume,
      }}
    >
      {children}
    </ResumeContext.Provider>
  );
}

export const useResume = () => {
  const context = useContext(ResumeContext);
  if (!context) {
    throw new Error('useResume must be used within a ResumeProvider');
  }
  return context;
};